






















































































import Vue from 'vue';
import { Component } from 'vue-property-decorator';
import { AccountModule } from '@/store/modules/account';
import { GetUserInfo } from '@/pages/user/api/login';
import { GetProductPage, GetProductSizeOptions } from '@/pages/user/api/product';
import { CancelPsProductFavorite, CreatePsProductFavorite, GetPsProductFavoriteStatus } from '../api/sc';
import { GetSkuCategoryOptions, SKU_CATEGORY_TYPE, SkuCategoryTreeItem } from '../api/sku-category';
import MicroSprayProductCard from '../components/micro-spray-product-card.vue';
import { psSupplierAdminEntryPath } from './admin/common';

type MediaItem = {
  id: string;
  name: string;
  imageUrl: string;
  supplierName: string;
  studioName: string;
  priceText: string;
  favorited: boolean;
  canPurchase: boolean;
};

type ProductPriceItem = {
  sizeOptionSn?: string;
  sizeName: string;
  unitPrice: number;
  sequence?: number;
};

type ProductSizeOption = {
  sn: string;
  name: string;
  sequence?: number;
};

type ProductCardResult = {
  productSn: string;
  name: string;
  imageUrl: string;
  studioName: string;
  supplierName: string;
  sizePriceList: ProductPriceItem[];
};

type PrintMediaListCache = {
  firstCategoryCode: string;
  secondCategoryCode: string;
  pageNum: number;
  pageSize: number;
  total: number;
  mediaList: MediaItem[];
  categoryTree: SkuCategoryTreeItem[];
  sizeNameMap: Record<string, string>;
  scrollY: number;
};

const PRINT_MEDIA_LIST_CACHE_KEY = 'print_media_list_state';

function normalizeArray<T>(value: T[] | null | undefined): T[] {
  return Array.isArray(value) ? value : [];
}

function normalizeCategoryOptions(list: SkuCategoryTreeItem[]): SkuCategoryTreeItem[] {
  return normalizeArray<SkuCategoryTreeItem>(list).map(item => {
    const children = normalizeCategoryOptions(item.children || []);
    return {
      ...item,
      children: children.length ? children : undefined,
    };
  });
}

@Component({
  components: {
    MicroSprayProductCard,
  },
})
export default class PrintMediaList extends Vue {
  loading = false;
  categoryLoading = false;
  mediaList: MediaItem[] = [];
  categoryTree: SkuCategoryTreeItem[] = [];
  firstCategoryCode = '';
  secondCategoryCode = '';
  sizeNameMap: Record<string, string> = {};
  enterpriseIsPs = false;
  pageNum = 1;
  pageSize = 9;
  total = 0;
  isRestoringListCache = false;
  cachedScrollY = 0;
  favoriteLoadingKey = '';

  async mounted() {
    const restored = this.restoreListCache();
    this.isRestoringListCache = restored;
    await Promise.all([this.fetchEnterpriseDetail(), this.fetchSizeOptionMap(), this.fetchCategoryOptions()]);
    if (restored && this.mediaList.length) {
      await this.fetchFavoriteStatus();
      this.restoreCachedScroll();
      this.isRestoringListCache = false;
      return;
    }
    this.isRestoringListCache = false;
    await this.fetchMediaList();
    this.restoreCachedScroll();
  }

  async activated() {
    await this.fetchEnterpriseDetail();
  }

  beforeDestroy() {
    this.saveListCache();
  }

  get currentUserInfo() {
    return AccountModule.currentUserInfo || {};
  }

  get supplierEntryLabel() {
    return this.isPsSupplier ? '供应商管理' : '供应商申请';
  }

  get isPsSupplier() {
    return this.enterpriseIsPs;
  }

  get categoryOptions() {
    return normalizeCategoryOptions(this.categoryTree);
  }

  get secondCategoryOptions() {
    const firstCategory = this.categoryOptions.find(item => item.code === this.firstCategoryCode);
    return normalizeArray<SkuCategoryTreeItem>(firstCategory && firstCategory.children);
  }

  handleSupplierEntry() {
    this.$router.push(this.isPsSupplier ? psSupplierAdminEntryPath : '/printMediaSupplierApply');
  }

  async fetchEnterpriseDetail() {
    if (!AccountModule.isLogined) {
      this.enterpriseIsPs = false;
      return;
    }
    try {
      const res = await GetUserInfo();
      await AccountModule.setUserInfo(res);
      const isPs = res && (res as any).isPs;
      this.enterpriseIsPs = isPs === true || isPs === 'true' || isPs === 1 || isPs === '1';
    } catch (error) {
      this.enterpriseIsPs = false;
      return;
    }
  }

  async fetchMediaList() {
    this.loading = true;
    try {
      const res = (await GetProductPage({
        pageNum: this.pageNum,
        pageSize: this.pageSize,
        firstCategoryCode: this.firstCategoryCode || undefined,
        secondCategoryCode: this.secondCategoryCode || undefined,
      })) as any;
      this.pageNum = Number((res && res.current) || this.pageNum);
      this.pageSize = Number((res && res.size) || this.pageSize);
      this.total = Number((res && res.total) || 0);
      const records = normalizeArray<ProductCardResult>(res && res.records);
      this.mediaList = records.map(item => ({
        id: item.productSn,
        name: item.name || '-',
        imageUrl: item.imageUrl || '',
        supplierName: item.supplierName || '',
        studioName: item.studioName || '',
        favorited: false,
        canPurchase: true,
        priceText: normalizeArray<ProductPriceItem>(item.sizePriceList)
          .sort((a, b) => (a.sequence || 0) - (b.sequence || 0))
          .map(price => `${this.resolveSizeName(price)}: ¥${Number(price.unitPrice || 0).toFixed(2)}`)
          .join(' / '),
      }));
      await this.fetchFavoriteStatus();
      this.saveListCache();
    } finally {
      this.loading = false;
    }
  }

  async fetchCategoryOptions() {
    this.categoryLoading = true;
    try {
      const res = await GetSkuCategoryOptions(SKU_CATEGORY_TYPE.SAMPLE);
      this.categoryTree = normalizeArray<SkuCategoryTreeItem>(res);
    } catch (error) {
      this.categoryTree = [];
    } finally {
      this.categoryLoading = false;
      this.saveListCache();
    }
  }

  async fetchSizeOptionMap() {
    try {
      const res = await GetProductSizeOptions();
      this.sizeNameMap = normalizeArray<ProductSizeOption>(res).reduce<Record<string, string>>((map, item) => {
        const sn = String(item.sn || '');
        if (sn) {
          map[sn] = String(item.name || '');
        }
        return map;
      }, {});
    } catch (error) {
      this.sizeNameMap = {};
    } finally {
      this.saveListCache();
    }
  }

  resolveSizeName(price: ProductPriceItem) {
    return price.sizeName || (price.sizeOptionSn ? this.sizeNameMap[price.sizeOptionSn] || price.sizeOptionSn : '--');
  }

  handleApplySample(item: MediaItem) {
    this.saveListCache();
    this.$router.push({
      path: '/sampleApply',
      query: {
        productSn: item.id,
      },
    });
  }

  async fetchFavoriteStatus() {
    if (!AccountModule.isLogined || !this.mediaList.length) {
      return;
    }
    const productSnList = this.mediaList.map(item => item.id).filter(Boolean);
    if (!productSnList.length) {
      return;
    }
    try {
      const res = await GetPsProductFavoriteStatus(productSnList);
      const favoritedSet = new Set(normalizeArray<any>(res).map(item => item.targetSn || item.target_sn).filter(Boolean));
      this.mediaList = this.mediaList.map(item => ({
        ...item,
        favorited: favoritedSet.has(item.id),
      }));
    } catch (error) {
      this.mediaList = this.mediaList.map(item => ({
        ...item,
        favorited: false,
      }));
    }
  }

  async handleToggleFavorite(item: MediaItem) {
    if (!item || !item.id || this.favoriteLoadingKey) {
      return;
    }
    if (!AccountModule.isLogined) {
      this.$message.warning('请先登录后收藏商品');
      this.$router.push({
        path: '/login',
        query: {
          redirect: this.$route.fullPath,
        },
      });
      return;
    }
    this.favoriteLoadingKey = item.id;
    try {
      if (item.favorited) {
        await CancelPsProductFavorite({ productSn: item.id });
        item.favorited = false;
        this.$message.success('已取消收藏');
      } else {
        await CreatePsProductFavorite({ productSn: item.id });
        item.favorited = true;
        this.$message.success('已收藏');
      }
      this.saveListCache();
    } finally {
      this.favoriteLoadingKey = '';
    }
  }

  handleFirstCategorySelect(code: string) {
    this.firstCategoryCode = this.firstCategoryCode === code ? '' : code;
    this.secondCategoryCode = '';
    this.pageNum = 1;
    this.fetchMediaList();
  }

  handleSecondCategorySelect(code: string) {
    this.secondCategoryCode = this.secondCategoryCode === code ? '' : code;
    this.pageNum = 1;
    this.fetchMediaList();
  }

  handlePageChange(page: number) {
    this.pageNum = page;
    this.fetchMediaList();
  }

  saveListCache() {
    const cache: PrintMediaListCache = {
      firstCategoryCode: this.firstCategoryCode,
      secondCategoryCode: this.secondCategoryCode,
      pageNum: this.pageNum,
      pageSize: this.pageSize,
      total: this.total,
      mediaList: this.mediaList,
      categoryTree: this.categoryTree,
      sizeNameMap: this.sizeNameMap,
      scrollY: this.isRestoringListCache ? this.cachedScrollY : this.getCurrentScrollY(),
    };
    try {
      window.sessionStorage.setItem(PRINT_MEDIA_LIST_CACHE_KEY, JSON.stringify(cache));
    } catch (error) {
      return;
    }
  }

  restoreListCache() {
    try {
      const rawCache = window.sessionStorage.getItem(PRINT_MEDIA_LIST_CACHE_KEY);
      if (!rawCache) {
        return false;
      }
      const cache = JSON.parse(rawCache) as Partial<PrintMediaListCache>;
      this.firstCategoryCode = String(cache.firstCategoryCode || '');
      this.secondCategoryCode = String(cache.secondCategoryCode || '');
      this.pageNum = Math.max(Number(cache.pageNum || 1), 1);
      this.pageSize = Math.max(Number(cache.pageSize || 9), 1);
      this.total = Math.max(Number(cache.total || 0), 0);
      this.mediaList = normalizeArray<MediaItem>(cache.mediaList);
      this.categoryTree = normalizeArray<SkuCategoryTreeItem>(cache.categoryTree);
      this.sizeNameMap = cache.sizeNameMap || {};
      this.cachedScrollY = Math.max(Number(cache.scrollY || 0), 0);
      return true;
    } catch (error) {
      window.sessionStorage.removeItem(PRINT_MEDIA_LIST_CACHE_KEY);
      return false;
    }
  }

  restoreCachedScroll() {
    try {
      const rawCache = window.sessionStorage.getItem(PRINT_MEDIA_LIST_CACHE_KEY);
      if (!rawCache) {
        return;
      }
      this.$nextTick(() => {
        window.scrollTo(0, this.cachedScrollY);
      });
    } catch (error) {
      return;
    }
  }

  getCurrentScrollY() {
    return window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0;
  }
}
